import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchBlogs, fetchBlogBySlug } from "../utils/blogsApi";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";

export default function BlogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    setStatus("loading");
    setArticle(null);

    fetchBlogBySlug(id, controller.signal)
      .then((found) => {
        setArticle(found);
        setStatus(found ? "ready" : "missing");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error("Failed to load blog", error);
        setStatus("missing");
      });

    // Related posts come from the first page of the list endpoint.
    fetchBlogs({ page: 1, pageSize: 4, signal: controller.signal })
      .then(({ blogs }) => {
        setRelatedArticles(blogs.filter((b) => b.slug !== id).slice(0, 3));
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error("Failed to load related blogs", error);
      });

    return () => controller.abort();
  }, [id]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white text-[#242424] font-sans antialiased flex flex-col justify-between">
        <Header />
        <main className="container mx-auto px-4 py-24 flex flex-col items-center gap-3">
          <span className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-[#5072F1] animate-spin" />
          <p className="text-xs text-gray-500">Loading article…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "missing" || !article) {
    return (
      <div className="min-h-screen bg-white text-[#242424] font-sans antialiased flex flex-col justify-between">
        <Header />
        <main className="container mx-auto px-4 py-20 text-center">
          <h2 className="text-xl font-bold mb-3">No Article Available</h2>
          <p className="text-sm text-gray-500 mb-5">
            The requested blog post is currently unavailable.
          </p>
          <button
            onClick={() => navigate("/blogs")}
            className="px-4 py-2 bg-[#5072F1] text-white rounded-lg text-xs font-semibold cursor-pointer"
          >
            ← Back to Blogs
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#242424] font-sans antialiased">
      {/* Main Navbar */}
      <Header />

      {/* Main Article Container */}
      <main className="container mx-auto px-4 md:px-8 pt-8 pb-12 md:pb-16 max-w-3xl">
        <button
          onClick={() => navigate("/blogs")}
          className="mb-5 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-black bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition-colors cursor-pointer"
        >
          ← Back to Blogs
        </button>

        {/* Title */}
        <div className="flex flex-col gap-3 mb-6">
          <h1 className="font-serif text-xl md:text-3xl font-bold text-[#1A1A1A] leading-snug">
            {article.title}
          </h1>

          {article.subtitle && (
            <p className="text-sm md:text-base text-gray-600 leading-relaxed font-sans font-normal">
              {article.subtitle}
            </p>
          )}

          {/* Author Header Bar */}
          <div className="flex items-center justify-between border-y border-gray-100 py-3 mt-1">
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#EFF4FF] text-[#5072F1] font-bold uppercase text-sm">
                {(article.author.name || "G").charAt(0)}
              </span>
              <div>
                <h4 className="font-semibold text-xs text-gray-900">
                  {article.author.name}
                </h4>
                <p className="text-[11px] text-gray-400">
                  {article.date} · {article.readTime}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  navigator.clipboard?.writeText(window.location.href)
                }
                className="p-1.5 text-gray-400 hover:text-black rounded-full hover:bg-gray-100 transition-colors"
                title="Copy Link"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {article.image && (
          <div className="mb-6 overflow-hidden rounded-xl bg-gray-100 max-h-[260px] md:max-h-[320px] w-full shadow-sm flex items-center justify-center">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Article Body Content */}
        <article className="prose prose-sm md:prose-base max-w-none text-[#292929] text-sm md:text-base leading-relaxed space-y-5">
          <div dangerouslySetInnerHTML={{ __html: article.content }} />
        </article>

        {/* Related Stories Section */}
        {relatedArticles.length > 0 && (
          <section className="mt-14 pt-10 border-t border-gray-200">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-6">
              What to read next
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <div
                  key={rel.id}
                  onClick={() => {
                    navigate(`/blogs/${rel.slug}`);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="group cursor-pointer flex flex-col justify-between p-3.5 rounded-lg border border-gray-100 hover:border-gray-300 hover:shadow-sm transition-all"
                >
                  <div>
                    <div className="aspect-[16/10] overflow-hidden rounded-md bg-gray-100 mb-2.5">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h4 className="font-bold text-xs md:text-sm text-gray-900 group-hover:text-[#5072F1] transition-colors line-clamp-2 mt-0.5">
                      {rel.title}
                    </h4>
                  </div>
                  <div className="mt-3 pt-2 border-t border-gray-50 text-[11px] text-gray-400">
                    {rel.author.name} · {rel.readTime}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
